import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

/**
 * A Statsig dynamic-config reference: the config key to read and a fallback used when the
 * key is unset (or resolves to an empty string).
 */
type DynamicConfig = {
	configKey: string;
	fallback: string;
};

/**
 * Reads a string value from Statsig dynamic config, falling back to `fallback` when the key
 * is unset or empty. Lets prompt copy be tuned remotely without a code deploy.
 */
export const resolveDynamicConfigStringValue = ({ configKey, fallback }: DynamicConfig): string =>
	StatsigConfigurations.getString(configKey, fallback) || fallback;

/**
 * Matches `{{VARIABLE}}` placeholders in a prompt template. The variable name is captured
 * so it can be swapped for a consumer-supplied value. Unknown tokens are left untouched.
 */
const PROMPT_TEMPLATE_TOKEN_PATTERN = /\{\{([A-Z0-9_]+)\}\}/gu;

/**
 * A dynamic-config-backed prompt template that declares the variables it expects.
 */
export type PromptTemplateConfig<TVariable extends string = never> = DynamicConfig & {
	variables: readonly TVariable[];
};

/**
 * Resolves a prompt template from dynamic config (falling back to `config.fallback`) and
 * substitutes each `{{VARIABLE}}` token with the value supplied by the consumer.
 *
 * This function is intentionally pure: obtaining variable values (querying, session data,
 * props, etc.) is the consumer's responsibility, not the prompt layer's.
 */
export const resolvePromptTemplate = <TVariable extends string>(
	config: PromptTemplateConfig<TVariable>,
	variables: Record<TVariable, string>,
): string => {
	const template = resolveDynamicConfigStringValue(config);

	return template.replace(PROMPT_TEMPLATE_TOKEN_PATTERN, (token, name: string) =>
		Object.prototype.hasOwnProperty.call(variables, name) ? variables[name] : token,
	);
};
